import { configureStore, type Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { baseApi } from './api/baseApi';
import authReducer, { setTokens, logout, isDifferentOwner } from './slices/authSlice';
import uiReducer from './slices/uiSlice';
import { reloadPage } from '@/utils/reload-once';

/**
 * Übernimmt dieser Reiter Token einer anderen Person, beginnt er von vorn.
 *
 * Testgrundlage 2.2, B-02: Nach dem Wechsel der Anmeldung im selben
 * Browserprofil übernahm ein offener Reiter das neue Token (unten, `storage`),
 * zeigte aber weiter die bisherige Person — in der Kopfzeile, im vorbelegten
 * Feld „Erklärende Person“ und in allen zwischengespeicherten Daten. Gespeichert
 * wurde schon unter der neuen. Wer das nicht bemerkt, handelt in fremdem Namen.
 *
 * Ein Abgleich Feld für Feld würde immer eine Stelle übersehen; deshalb lädt
 * die Seite vollständig neu, sobald das Token einer anderen Person oder einem
 * anderen Mandanten gehört. Verglichen wird mit dem letzten Token, das dieser
 * Reiter hatte — auch wenn er inzwischen abgemeldet war: Was eine abgemeldete
 * Seite noch zwischengespeichert hat, gehört der vorigen Person. Bleibt es
 * dieselbe Person (Erneuerung), ändert sich nichts.
 */
let letztesToken: string | null = null;
const personenwechsel: Middleware = (api) => (next) => (action) => {
  const vorher = (api.getState() as { auth: { accessToken: string | null } }).auth.accessToken ?? letztesToken;
  const ergebnis = next(action);
  const nachher = (api.getState() as { auth: { accessToken: string | null } }).auth.accessToken;
  if (nachher) {
    letztesToken = nachher;
    if (setTokens.match(action) && isDifferentOwner(vorher, nachher)) reloadPage();
  }
  return ergebnis;
};

export const store = configureStore({
  reducer: {
    [baseApi.reducerPath]: baseApi.reducer,
    auth: authReducer,
    ui: uiReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).concat(baseApi.middleware, personenwechsel),
  devTools: import.meta.env.DEV,
});

// Fokus und Verbindung melden — darauf laden offene Abfragen nach (baseApi.ts,
// refetchOnFocus/refetchOnReconnect; Testprotokoll TG 2.4, T33).
setupListeners(store.dispatch);

/**
 * Was der Reiter nebenan tut, gilt auch hier.
 *
 * Jeder Reiter hält seine eigene Kopie der Token. Erneuert einer, ist die
 * Kopie des anderen veraltet — und legt er sie vor, wertet der Dienst das zu
 * Recht als Wiederverwendung und beendet alle Sitzungen. Das Browserereignis
 * `storage` meldet jede Änderung aus einem anderen Reiter; hier wird sie
 * übernommen, damit niemand mit einem überholten Token arbeitet. Meldet sich
 * ein Reiter ab, folgen die übrigen. Gehört das neue Token einer anderen
 * Person, lädt die Seite neu (`personenwechsel` oben).
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (ereignis) => {
    if (ereignis.key !== 'refreshToken' && ereignis.key !== 'accessToken') return;
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    if (accessToken && refreshToken) {
      if (refreshToken !== store.getState().auth.refreshToken) {
        store.dispatch(setTokens({ accessToken, refreshToken }));
      }
    } else if (store.getState().auth.isAuthenticated) {
      store.dispatch(logout());
      // Abgemeldet heißt auch: nichts mehr von der Person im Zwischenspeicher.
      // Sonst sähe, wer sich danach anmeldet, deren Daten (B-02).
      store.dispatch(baseApi.util.resetApiState());
    }
  });
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
